// The tour: a right-hand drawer, one slide per milestone.
//
// It deliberately does not cover the screen. The thing each slide is asking for
// — the Connect button, the composer — is on the page behind it, and a panel
// that hides the target makes people dismiss it in order to obey it. So the
// drawer slides in beside the work, and its primary button walks straight to it.

import { useCallback, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MILESTONES, MILESTONE_COUNT } from "./milestones";
import { MILESTONE_ICON } from "./icons";
import { useEngagement } from "./EngagementContext";

const FOCUSABLE =
  'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

export function GettingStarted() {
  const { tourSlide, openTour, closeTour, progress } = useEngagement();
  const navigate = useNavigate();
  const panel = useRef<HTMLElement>(null);
  const returnTo = useRef<HTMLElement | null>(null);

  const open = tourSlide !== null;
  const index = tourSlide ?? 0;
  const milestone = MILESTONES[index];
  const state = progress?.steps[index] ?? null;

  const go = useCallback((to: number) => {
    openTour(Math.min(Math.max(to, 0), MILESTONE_COUNT - 1));
  }, [openTour]);

  // Focus moves into the drawer when it opens and back to whatever opened it
  // when it closes, because a panel that steals focus and never returns it
  // leaves a keyboard user at the top of the document.
  useEffect(() => {
    if (!open) return;
    returnTo.current = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    return () => returnTo.current?.focus?.();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { closeTour(); return; }
      if (e.key === "ArrowRight") { go(index + 1); return; }
      if (e.key === "ArrowLeft") { go(index - 1); return; }
      if (e.key !== "Tab" || !panel.current) return;

      const items = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)]
        .filter((el) => el.offsetParent !== null);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, index, go, closeTour]);

  if (!open) return null;

  const Icon = MILESTONE_ICON[milestone.id];
  const done = state?.done ?? false;
  // A step this person may not perform is not an outstanding task of theirs. It
  // is still worth explaining, because they will see its results.
  const delegated = state ? !state.allowed : false;

  function act() {
    closeTour();
    navigate(milestone.route);
  }

  return (
    <div className="drawer-backdrop" onClick={closeTour}>
      <aside
        className="side-drawer eng-tour"
        role="dialog"
        aria-modal="true"
        aria-labelledby="eng-tour-title"
        tabIndex={-1}
        ref={panel}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="drawer-head">
          <div className="eng-tour-head">
            <span className="eng-card-mark eng-tour-mark" aria-hidden="true">
              {milestone.step}
            </span>
            <div>
              <p className="eng-step-count">Step {milestone.step} of {MILESTONE_COUNT}</p>
              <h2 id="eng-tour-title">{milestone.title}</h2>
            </div>
          </div>
          <button type="button" className="drawer-close" onClick={closeTour} aria-label="Close">
            &times;
          </button>
        </div>

        <div className="eng-tour-body">
          <div className="eng-tour-art" aria-hidden="true"><Icon /></div>

          {done && <p className="eng-chip done">Done</p>}
          {!done && delegated && <p className="eng-chip delegated">Someone else does this</p>}

          <p className="eng-lede">{milestone.lede}</p>

          <ul className="eng-points">
            {milestone.points.map((point) => <li key={point}>{point}</li>)}
          </ul>

          {delegated && <p className="notice eng-delegated-note">{milestone.delegated}</p>}
        </div>

        <div className="eng-tour-foot">
          {!delegated && (
            <button type="button" className="primary eng-cta" onClick={act}>
              {done ? `Go to ${milestone.title.toLowerCase()}` : milestone.cta}
            </button>
          )}

          <div className="eng-nav">
            <button type="button" className="sm" disabled={index === 0} onClick={() => go(index - 1)}>
              Back
            </button>

            {/* Plain buttons rather than a tablist: there are no tab panels
                here, only one slide that is replaced, and claiming otherwise
                makes a screen reader promise navigation that does not exist. */}
            <div className="eng-dots">
              {MILESTONES.map((m, i) => (
                <button
                  key={m.id}
                  type="button"
                  aria-current={i === index ? "step" : undefined}
                  aria-label={`Step ${m.step}: ${m.title}`}
                  className={
                    `eng-dot${i === index ? " on" : ""}${progress?.steps[i]?.done ? " done" : ""}`
                  }
                  onClick={() => go(i)}
                />
              ))}
            </div>

            {index === MILESTONE_COUNT - 1
              ? <button type="button" className="sm" onClick={closeTour}>Finish</button>
              : <button type="button" className="sm" onClick={() => go(index + 1)}>Next</button>}
          </div>

          <p className="eng-foot-note">
            The longer version, with pictures, is on{" "}
            <Link to="/how-it-works" onClick={closeTour}>How it works</Link>.
          </p>
        </div>
      </aside>
    </div>
  );
}
